// src/utils/timelineHelper.js

/**
 * Détermine la couleur hexadécimale exacte d'un mode de session (Aligné sur Android et mapHelper)
 * - Rouge Alerte : #D32F2F
 * - Jaune Partage : #FFD60A
 * - Bleu Cyan Local : #00BCD4
 */
export function getSessionTypeColor(sessionType) {
    const st = String(sessionType || '').toUpperCase();
    if (
        st === "ALERT" ||
        st === "LIVEMAPALERTE" ||
        st.includes("ALERT") ||
        st.includes("MANUAL") ||
        st.includes("DETECTED") ||
        st.includes("SHORTCUT") ||
        st.includes("WIDGET") ||
        st.includes("WATCH") ||
        st.includes("MISMATCH") ||
        st.endsWith("_STOPPED")
    ) {
        return "#D32F2F"; // Rouge Alerte
    } else if (st === "SHARE" || st === "LIVEMAPSHARE") {
        return "#FFD60A"; // Jaune Partage
    }
    return "#00BCD4"; // Bleu Cyan (Normal / Suivi / Itinéraire)
}

/**
 * Couleurs des passages, alignées sur l'application : suivi de position ardoise, itinéraire bleu
 * roi, parcours vert. La trace garde la couleur de l'état (local, partage, alerte).
 */
export const PASSAGE_COLORS = { follow: '#64748B', itinerary: '#2563EB', trail: '#16A34A', meeting: '#EC4899' };

/** Couleur du tracé prévu et de son corridor : parcours en randonnée / course à pied, itinéraire sinon. */
export function routePassageColor(travelMode) {
    const mode = String(travelMode || '').toUpperCase();
    return mode === 'HIKE' || mode === 'RUN' ? PASSAGE_COLORS.trail : PASSAGE_COLORS.itinerary;
}

/**
 * Calcule les segments de timeline et l'offset média pour le rendu CSS.
 */
export function computeTimelineSegments(input, defaultSessionType = null) {
    const segments = Array.isArray(input) ? input : (input.segments || []);
    const mediaStartTs = Array.isArray(input) ? null : (input.mediaStartTs || null);
    const defaultColor = (!Array.isArray(input) && input.defaultColor)
        ? input.defaultColor
        : getSessionTypeColor(defaultSessionType);

    const resolveColor = (color) => {
        if (!color) return defaultColor;
        if (color.startsWith('#')) return color;
        if (color === 'red') return '#D32F2F';
        if (color === 'yellow') return '#FFD60A';
        return '#00BCD4';
    };

    if (!segments.length) {
        const color = resolveColor(defaultColor);
        return { duration: 0, gradient: `linear-gradient(to right, ${color} 0%, ${color} 100%)` };
    }

    const minTs = (!Array.isArray(input) && Number.isFinite(Number(input.minTs)))
        ? Number(input.minTs)
        : segments[0].startTs;
    const maxTs = (!Array.isArray(input) && Number.isFinite(Number(input.maxTs)))
        ? Number(input.maxTs)
        : segments[segments.length - 1].endTs;
    const totalDuration = maxTs - minTs;

    if (totalDuration === 0) {
        const color = resolveColor(segments[0].color || defaultColor);
        return { duration: 0, minTs, maxTs, gradient: `linear-gradient(to right, ${color} 0%, ${color} 100%)` };
    }

    // Construire le gradient linéaire CSS exact
    const gradientStops = [];

    segments.forEach((seg, i) => {
        const startPercent = ((seg.startTs - minTs) / totalDuration) * 100;
        const nextStartTs = i < segments.length - 1 ? segments[i + 1].startTs : seg.endTs;
        const endPercent = ((nextStartTs - minTs) / totalDuration) * 100;

        const color = resolveColor(seg.color);

        gradientStops.push(`${color} ${startPercent.toFixed(2)}%`);
        gradientStops.push(`${color} ${endPercent.toFixed(2)}%`);
    });

    const gradientStr = `linear-gradient(to right, ${gradientStops.join(', ')})`;

    // Calculer l'offset média :
    let mediaOffsetMs = null;

    if (mediaStartTs) {
        mediaOffsetMs = mediaStartTs - minTs;
    } else {
        const firstRed = segments.find(s => resolveColor(s.color) === '#D32F2F');
        if (firstRed) mediaOffsetMs = firstRed.startTs - minTs;
    }

    const mediaOffsetSeconds = mediaOffsetMs != null ? Math.max(0, mediaOffsetMs / 1000) : 0;

    return {
        duration: totalDuration / 1000,
        minTs,
        maxTs,
        mediaOffsetSeconds,
        gradient: gradientStr
    };
}
